import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  type PaginationProps,
  type TableColumnsType,
  Tooltip,
} from 'antd';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import {
  searchFilterByProperty,
  sortDates,
  sortStrings,
} from 'pos-client/utils/dataTableHelpers.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import type { BaseAssetTypeModel } from 'pos-shared/models/assetTypesSchema.ts';

import { AssetTypesModal } from './Modals/AssetTypesModal.tsx';

interface AssetsTableProps {
  data: BaseAssetTypeModel[];
  isLoading: boolean;
  pagination: PaginationProps | undefined;
}

export const AssetTypesTable = ({
  data,
  isLoading,
  pagination,
}: AssetsTableProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<
    BaseAssetTypeModel | undefined
  >();

  const showModal = (record: BaseAssetTypeModel & { key?: string }) => {
    setIsModalOpen(true);
    setEditModalData(record);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const columns: TableColumnsType<BaseAssetTypeModel> = [
    {
      title: t('assets.assetType'),
      key: 'name',
      dataIndex: 'name',
      ...searchFilterByProperty<Pick<BaseAssetTypeModel, 'name'>>('name'),
    },
    {
      title: t('common.words.description'),
      key: 'description',
      dataIndex: 'description',
      render: (value) => {
        if (!value) {
          return (
            <Tooltip title={t('assets.noReferenceFound')}>
              <span>-</span>
            </Tooltip>
          );
        } else {
          return <span>{value}</span>;
        }
      },
      ...searchFilterByProperty<Pick<BaseAssetTypeModel, 'description'>>(
        'description'
      ),
    },
    {
      title: t('assets.serviceTypes'),
      key: 'serviceType',
      dataIndex: 'serviceType',
      render: (value) => {
        return <span>{value}</span>;
      },
      sorter: (a, b) =>
        sortStrings<Omit<BaseAssetTypeModel, 'id' | 'lastModified'>>(
          a,
          b,
          'serviceType'
        ),
    },
    {
      title: t('common.audit.dateModified'),
      key: 'lastModified',
      dataIndex: 'lastModified',
      responsive: ['lg'],
      render: (value) =>
        value ? (
          <span>{convertISOStringToLocalDate(value)}</span>
        ) : (
          <Tooltip title={t('assets.noDateSpecified') as string}>
            <span>-</span>
          </Tooltip>
        ),
      sorter: (a, b) =>
        sortDates<Pick<BaseAssetTypeModel, 'lastModified'>>(
          a,
          b,
          'lastModified'
        ),
    },
    {
      title: '',
      key: 'editActionColumn',
      width: 60,
      render: (_, record) => (
        <Button size="small" onClick={() => showModal(record)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PosTable
        id="location-asset-type-results-table"
        dataSource={data}
        columns={columns}
        isLoading={isLoading}
        paging={pagination}
      />
      {isModalOpen && (
        <AssetTypesModal
          data={editModalData}
          handleCancel={handleCancel}
          handleOk={handleOk}
        />
      )}
    </>
  );
};
